/**
 * Item-based ("modular") section editor — the top-down alternative to the
 * freeform {@link SectionWysiwyg}. Decomposes a section into discrete cards
 * (name + one-line slug + collapsible details), up to two levels (group →
 * child), and serialises edits back to the same markdown `value`/`onChange`
 * contract — so it plugs straight into the existing debounced persist path
 * with no IPC changes (the `.md` stays the single source of truth).
 *
 * State model: the parsed item tree is held locally and re-synced from `value`
 * only when `value` changes *externally* (section switch, applied AI draft),
 * mirroring SectionWysiwyg's re-entrancy guard. Editing a name in place must
 * not re-parse and re-key the tree mid-keystroke.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import {
  ChevronRight,
  ChevronDown,
  Plus,
  Trash2,
  ArrowUp,
  ArrowDown,
  Sparkles,
  ListPlus,
  Wand2,
} from 'lucide-react';
import { SectionWysiwyg } from './SectionWysiwyg';
import { Tip } from './Tip';
import { ItemProposalDialog } from './ItemProposalDialog';
import { proposeItemDetails, proposeItemSlug, proposeItemEntries, reviseItem } from './lib/item-ai';
import {
  parseItems,
  serializeItems,
  blankItem,
  diffChangedItemIds,
  withGameLoopFirst,
  isGameLoopItem,
  GAME_LOOP_NAME,
  type DesignItem,
  type ParsedSection,
} from './lib/section-items';

export interface SectionItemsEditorProps {
  value: string;
  onChange: (markdown: string) => void;
  /** AI context. When `gameId` is present, the bi-directional slug ↔ details
   *  affordances (Sparkles) are shown; absent (e.g. in tests), they're hidden. */
  gameId?: string;
  sectionTitle?: string;
  projectName?: string | null;
  /** Last-built snapshot body for this section; items differing from it get an
   *  "edited since last build" pip. Undefined → no snapshot, no pips. */
  baselineBody?: string;
  /** When true (the Systems section), the "Game Loop" item is pinned to the top
   *  and locked, and an "Add Game Loop" affordance shows when one is absent. */
  hostsGameLoop?: boolean;
}

/** Per-item AI actions; supplied by the editor, invoked by each card. */
interface ItemAiHandlers {
  requestDetails: (
    item: DesignItem,
    groupName: string | null,
    apply: (body: string) => void,
  ) => void;
  requestSlug: (item: DesignItem, groupName: string | null, apply: (slug: string) => void) => void;
  /** Freeform "Revise this item" — rewrites slug + details, and for a top-level
   *  item its sub-items too (the model may add / edit / remove `###` entries). */
  requestRevise: (
    item: DesignItem,
    groupName: string | null,
    apply: (patch: { slug: string; body: string; children?: DesignItem[] }) => void,
  ) => void;
  requestEntries: (
    item: DesignItem,
    apply: (patch: { body: string; children: DesignItem[] }) => void,
  ) => void;
}

// --- preamble (leading H1 + optional intro prose) -------------------------

interface Preamble {
  head: string; // the leading "# Title" line, preserved verbatim ('' if none)
  intro: string; // intro prose after the title ('' if none)
}

function splitPreamble(preamble: string): Preamble {
  const lines = preamble.split('\n');
  if (lines[0] && /^#\s/.test(lines[0])) {
    return { head: lines[0], intro: lines.slice(1).join('\n').trim() };
  }
  return { head: '', intro: preamble.trim() };
}

function joinPreamble(p: Preamble): string {
  return [p.head, p.intro].filter((s) => s.trim()).join('\n\n');
}

// --- id minting (unique within the current tree) --------------------------

function collectIds(items: DesignItem[]): Set<string> {
  const ids = new Set<string>();
  for (const it of items) {
    ids.add(it.id);
    for (const c of it.children) ids.add(c.id);
  }
  return ids;
}

function mintId(existing: Set<string>, base = 'item'): string {
  let id = base;
  let n = 2;
  while (existing.has(id)) {
    id = `${base}-${n}`;
    n += 1;
  }
  existing.add(id);
  return id;
}

// --- immutable tree helpers -----------------------------------------------

function replaceAt<T>(arr: T[], index: number, next: T): T[] {
  const copy = arr.slice();
  copy[index] = next;
  return copy;
}

function removeAt<T>(arr: T[], index: number): T[] {
  const copy = arr.slice();
  copy.splice(index, 1);
  return copy;
}

function move<T>(arr: T[], index: number, delta: number): T[] {
  const to = index + delta;
  if (to < 0 || to >= arr.length) return arr;
  const copy = arr.slice();
  const [m] = copy.splice(index, 1);
  copy.splice(to, 0, m);
  return copy;
}

// --- card -----------------------------------------------------------------

interface ItemCardProps {
  item: DesignItem;
  level: 2 | 3;
  /** Parent group name when this is a child card (for AI context); else null. */
  groupName: string | null;
  expanded: Set<string>;
  toggleExpanded: (id: string) => void;
  /** Ids whose parent details editor is revealed (groups hide it by default). */
  detailsExpanded: Set<string>;
  toggleDetails: (id: string) => void;
  /** Ids edited since the last build — render the amber pip. */
  changedIds: Set<string>;
  onChange: (next: DesignItem) => void;
  onDelete: () => void;
  onMove: (delta: number) => void;
  onAddChild?: () => void;
  ai?: ItemAiHandlers;
  testId: string;
  /** The pinned Game Loop card: name is read-only and it can't be moved or
   *  deleted (it's structural — always the first Systems item). */
  locked?: boolean;
}

function ItemCard({
  item,
  level,
  groupName,
  expanded,
  toggleExpanded,
  detailsExpanded,
  toggleDetails,
  changedIds,
  onChange,
  onDelete,
  onMove,
  onAddChild,
  ai,
  testId,
  locked = false,
}: ItemCardProps): ReactElement {
  const isOpen = expanded.has(item.id);
  const changed = changedIds.has(item.id);
  const hasChildren = item.children.length > 0;
  const isGroup = level === 2 && hasChildren;
  const nameClass = level === 2 ? 'text-base font-semibold' : 'text-sm font-medium';
  // Leaf items and child cards show their editor on expand (the body is the
  // content); a group hides the parent editor behind a toggle so you can browse
  // its nested items without mounting it.
  const showDetails = !isGroup || detailsExpanded.has(item.id);

  const confirmDelete = (): void => {
    const hasContent = item.body.trim() || item.children.length > 0 || item.slug.trim();
    if (hasContent && !window.confirm(`Delete "${item.name}"? This can't be undone here.`)) return;
    onDelete();
  };

  // Nested entries + their add/suggest controls. Rendered before the parent
  // details for a group, after it for a leaf.
  const entriesSection = (
    <div className="space-y-2">
      {item.children.map((child, ci) => (
        <ItemCard
          key={child.id}
          item={child}
          level={3}
          groupName={item.name}
          expanded={expanded}
          toggleExpanded={toggleExpanded}
          detailsExpanded={detailsExpanded}
          toggleDetails={toggleDetails}
          changedIds={changedIds}
          ai={ai}
          testId={`${testId}-child-${ci}`}
          onChange={(next) => onChange({ ...item, children: replaceAt(item.children, ci, next) })}
          onDelete={() => onChange({ ...item, children: removeAt(item.children, ci) })}
          onMove={(delta) => onChange({ ...item, children: move(item.children, ci, delta) })}
        />
      ))}
      <div className="flex items-center gap-2">
        {onAddChild && hasChildren && (
          <button
            type="button"
            data-testid={`${testId}-add-child`}
            onClick={onAddChild}
            className="flex items-center gap-1 rounded border border-dashed border-crucible-border px-2 py-1 text-xs text-crucible-muted hover:text-crucible-fg"
          >
            <Plus size={12} /> Add entry
          </button>
        )}
        {ai && (
          <Tip label="Let the Co-Designer propose the named entries for this heading" align="end">
            <button
              type="button"
              data-testid={`${testId}-ai-entries`}
              onClick={() =>
                ai.requestEntries(item, ({ body, children }) =>
                  onChange({ ...item, body, children }),
                )
              }
              className="peer flex items-center gap-1 rounded border border-dashed border-crucible-border px-2 py-1 text-xs text-crucible-muted hover:text-crucible-accent-hover"
            >
              <Sparkles size={12} /> Suggest entries
            </button>
          </Tip>
        )}
      </div>
    </div>
  );

  return (
    <div
      data-testid={testId}
      className={`rounded-md border border-crucible-border ${
        level === 3 ? 'bg-crucible-bg' : 'bg-crucible-bg-elevated'
      }`}
    >
      <div className="flex items-start gap-2 p-3">
        <Tip label={isOpen ? 'Collapse details' : 'Expand details'} align="start" className="mt-1">
          <button
            type="button"
            aria-label={isOpen ? 'Collapse details' : 'Expand details'}
            aria-expanded={isOpen}
            onClick={() => toggleExpanded(item.id)}
            className="peer text-crucible-muted hover:text-crucible-fg"
          >
            {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          </button>
        </Tip>

        {changed && (
          <span
            title="Edited since the last build"
            data-testid={`${testId}-edited`}
            className="mt-2 size-1.5 shrink-0 rounded-full bg-amber-300"
          />
        )}

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <input
              data-testid={`${testId}-name`}
              value={item.name}
              placeholder="Name"
              readOnly={locked}
              onChange={(e) => onChange({ ...item, name: e.currentTarget.value })}
              className={`min-w-0 flex-1 bg-transparent text-crucible-fg outline-none ${nameClass} ${
                locked ? 'cursor-default' : ''
              }`}
            />
            {locked && (
              <span className="shrink-0 rounded bg-crucible-accent/15 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-crucible-accent">
                Core loop
              </span>
            )}
          </div>
          <input
            data-testid={`${testId}-slug`}
            value={item.slug}
            placeholder="One-line description…"
            onChange={(e) => onChange({ ...item, slug: e.currentTarget.value })}
            className="mt-0.5 w-full bg-transparent text-sm italic text-crucible-muted outline-none"
          />
        </div>

        <div className="flex items-center gap-1 text-crucible-muted">
          {level === 2 && onAddChild && (
            <Tip label="Add a named entry under this heading (e.g. a level or item)" align="end">
              <button
                type="button"
                aria-label="Add entry"
                data-testid={`${testId}-add-entry`}
                onClick={onAddChild}
                className="peer hover:text-crucible-accent-hover"
              >
                <ListPlus size={14} />
              </button>
            </Tip>
          )}
          {ai && (
            <Tip
              label="Revise this item — describe a change and update its description + details"
              align="end"
            >
              <button
                type="button"
                aria-label="Revise item"
                data-testid={`${testId}-ai-revise`}
                onClick={() =>
                  ai.requestRevise(item, groupName, (patch) => onChange({ ...item, ...patch }))
                }
                className="peer hover:text-crucible-accent-hover"
              >
                <Wand2 size={14} />
              </button>
            </Tip>
          )}
          {ai && (
            <Tip label="Generate details from the one-line description" align="end">
              <button
                type="button"
                aria-label="Generate details from description"
                data-testid={`${testId}-ai-details`}
                onClick={() =>
                  ai.requestDetails(item, groupName, (body) => onChange({ ...item, body }))
                }
                className="peer hover:text-crucible-accent-hover"
              >
                <Sparkles size={14} />
              </button>
            </Tip>
          )}
          {!locked && (
            <>
              <Tip label="Move up" align="end">
                <button
                  type="button"
                  aria-label="Move up"
                  onClick={() => onMove(-1)}
                  className="peer hover:text-crucible-fg"
                >
                  <ArrowUp size={14} />
                </button>
              </Tip>
              <Tip label="Move down" align="end">
                <button
                  type="button"
                  aria-label="Move down"
                  onClick={() => onMove(1)}
                  className="peer hover:text-crucible-fg"
                >
                  <ArrowDown size={14} />
                </button>
              </Tip>
              <Tip label="Delete" align="end">
                <button
                  type="button"
                  aria-label="Delete"
                  onClick={confirmDelete}
                  className="peer hover:text-red-400"
                >
                  <Trash2 size={14} />
                </button>
              </Tip>
            </>
          )}
        </div>
      </div>

      {isOpen && (
        <div className="space-y-3 border-t border-crucible-border px-3 pb-3 pt-2">
          {/* Group: nested entries first, so they're reachable without the
              parent editor. */}
          {isGroup && entriesSection}

          {/* The heading's own details: a leaf/child shows its editor on
              expand; a group keeps it behind this toggle. */}
          {isGroup && (
            <Tip
              label={
                showDetails ? "Hide this heading's own details" : "Edit this heading's own details"
              }
              align="start"
            >
              <button
                type="button"
                data-testid={`${testId}-details-toggle`}
                onClick={() => toggleDetails(item.id)}
                className="peer flex items-center gap-1 text-xs text-crucible-muted hover:text-crucible-fg"
              >
                {showDetails ? <ChevronDown size={12} /> : <ChevronRight size={12} />} Details
              </button>
            </Tip>
          )}

          {showDetails && (
            <div className="space-y-2">
              {ai && (
                <div className="flex justify-end">
                  <Tip label="Rewrite the one-line description from the details below" align="end">
                    <button
                      type="button"
                      data-testid={`${testId}-ai-slug`}
                      onClick={() =>
                        ai.requestSlug(item, groupName, (slug) => onChange({ ...item, slug }))
                      }
                      className="peer flex items-center gap-1 text-xs text-crucible-muted hover:text-crucible-accent-hover"
                    >
                      <Sparkles size={12} /> Refresh description from details
                    </button>
                  </Tip>
                </div>
              )}
              <div className="flex h-64 flex-col overflow-hidden rounded border border-crucible-border bg-crucible-bg">
                <SectionWysiwyg
                  value={item.body}
                  onChange={(body) => onChange({ ...item, body })}
                />
              </div>
            </div>
          )}

          {/* Leaf (level 2, no children yet): entry controls below the editor
              so the first nested entry can be added. */}
          {level === 2 && !hasChildren && entriesSection}
        </div>
      )}
    </div>
  );
}

// --- editor ---------------------------------------------------------------

interface ProposalState {
  title: string;
  applyLabel: string;
  original: string;
  generate: (instruction: string) => Promise<string>;
  apply: (text: string) => void;
  /** When set, the dialog first asks for a freeform instruction (Revise). */
  instruction?: { prompt: string; placeholder?: string; submitLabel?: string };
}

export function SectionItemsEditor({
  value,
  onChange,
  gameId,
  sectionTitle,
  projectName,
  baselineBody,
  hostsGameLoop = false,
}: SectionItemsEditorProps): ReactElement {
  // In the loop-hosting (Systems) section, keep the Game Loop item pinned first.
  const pinLoop = useCallback(
    (parsed: ParsedSection): ParsedSection => {
      if (!hostsGameLoop) return parsed;
      const items = withGameLoopFirst(parsed.items);
      return items === parsed.items ? parsed : { ...parsed, items };
    },
    [hostsGameLoop],
  );
  const [section, setSection] = useState<ParsedSection>(() => pinLoop(parseItems(value)));
  const changedIds = useMemo(
    () =>
      baselineBody == null
        ? new Set<string>()
        : diffChangedItemIds(section, parseItems(baselineBody)),
    [section, baselineBody],
  );
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
  const [detailsExpanded, setDetailsExpanded] = useState<Set<string>>(() => new Set());
  const [introOpen, setIntroOpen] = useState(false);
  const [proposal, setProposal] = useState<ProposalState | null>(null);
  // The markdown we last emitted — guards the external-resync effect from
  // clobbering local edits with the echo of our own onChange.
  const lastEmitted = useRef<string>(value);

  useEffect(() => {
    if (value === lastEmitted.current) return;
    lastEmitted.current = value;
    setSection(pinLoop(parseItems(value)));
  }, [value, pinLoop]);

  // If the loop-hosting flag turns on without the markdown changing, the resync
  // effect above short-circuits on the unchanged value — re-pin here so the
  // Game Loop still floats first. No-op (same reference) once already pinned.
  useEffect(() => {
    setSection((prev) => pinLoop(prev));
  }, [pinLoop]);

  const commit = useCallback(
    (next: ParsedSection) => {
      const pinned = pinLoop(next);
      setSection(pinned);
      const md = serializeItems(pinned);
      lastEmitted.current = md;
      onChange(md);
    },
    [onChange, pinLoop],
  );

  const toggleExpanded = useCallback((id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleDetails = useCallback((id: string) => {
    setDetailsExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const pre = splitPreamble(section.preamble);

  const setItems = (items: DesignItem[]): void => commit({ ...section, items });

  const addItem = (): void => {
    const id = mintId(collectIds(section.items), 'item');
    const item: DesignItem = { ...blankItem('New item'), id };
    commit({ ...section, items: [...section.items, item] });
    setExpanded((prev) => new Set(prev).add(id));
  };

  const hasGameLoop = section.items.some((it) => isGameLoopItem(it.name));
  const addGameLoop = (): void => {
    const id = mintId(collectIds(section.items), 'game-loop');
    const loop: DesignItem = { ...blankItem(GAME_LOOP_NAME), id };
    // pinLoop in commit floats it to the front regardless of insert position.
    commit({ ...section, items: [loop, ...section.items] });
    setExpanded((prev) => new Set(prev).add(id));
  };

  const addGroup = (): void => {
    const ids = collectIds(section.items);
    const groupId = mintId(ids, 'group');
    const childId = mintId(ids, 'item');
    const group: DesignItem = {
      ...blankItem('New group'),
      id: groupId,
      children: [{ ...blankItem('New item'), id: childId }],
    };
    commit({ ...section, items: [...section.items, group] });
    setExpanded((prev) => new Set(prev).add(groupId));
  };

  const addChildTo = (index: number): void => {
    const item = section.items[index];
    const childId = mintId(collectIds(section.items), 'item');
    const next = {
      ...item,
      children: [...item.children, { ...blankItem('New item'), id: childId }],
    };
    commit({ ...section, items: replaceAt(section.items, index, next) });
    setExpanded((prev) => new Set(prev).add(item.id));
  };

  const sectionLabel = sectionTitle ?? '';
  const ai: ItemAiHandlers | undefined = gameId
    ? {
        requestDetails: (item, groupName, apply) =>
          setProposal({
            title: `Update “${item.name}” details`,
            applyLabel: 'Update details',
            original: item.body,
            generate: () =>
              proposeItemDetails(gameId, {
                projectName: projectName ?? null,
                sectionTitle: sectionLabel,
                groupName,
                itemName: item.name,
                slug: item.slug,
                body: item.body,
              }),
            apply,
          }),
        requestSlug: (item, groupName, apply) =>
          setProposal({
            title: `Refresh “${item.name}” description`,
            applyLabel: 'Use description',
            original: item.slug,
            generate: () =>
              proposeItemSlug(gameId, {
                projectName: projectName ?? null,
                sectionTitle: sectionLabel,
                groupName,
                itemName: item.name,
                slug: item.slug,
                body: item.body,
              }),
            apply,
          }),
        requestRevise: (item, groupName, apply) => {
          // A top-level item (no parent group) can have its sub-items revised;
          // a child card (groupName set) is a leaf — slug + details only.
          const allowChildren = groupName === null;
          const currentItemMarkdown = serializeItems({ preamble: '', items: [item] });
          setProposal({
            title: `Revise “${item.name}”`,
            applyLabel: 'Apply revision',
            instruction: {
              prompt: allowChildren
                ? `Describe the change to “${item.name}”. The Co-Designer rewrites its description, details, and sub-items — it can add, edit, or remove entries (e.g. questions).`
                : `Describe the change to “${item.name}”. The Co-Designer rewrites its description and details together.`,
              placeholder: allowChildren
                ? 'e.g. add 5 harder questions, drop the easy ones, add a 30s timer'
                : 'e.g. make it harder, add a 30s timer, swap the reward to coins',
            },
            // Diff the WHOLE item (slug + body + sub-items) so every change shows.
            original: currentItemMarkdown,
            generate: async (instruction) => {
              const revised = await reviseItem(
                gameId,
                {
                  projectName: projectName ?? null,
                  sectionTitle: sectionLabel,
                  groupName,
                  itemName: item.name,
                  slug: item.slug,
                  body: item.body,
                },
                instruction,
                { currentItemMarkdown, allowChildren },
              );
              if (!revised) {
                throw new Error(
                  "The Co-Designer didn't return a revised item — try rephrasing the instruction.",
                );
              }
              return serializeItems({
                preamble: '',
                items: [
                  { ...item, slug: revised.slug, body: revised.body, children: revised.children },
                ],
              });
            },
            apply: (text) => {
              const top = parseItems(text).items[0];
              if (!top) return;
              apply(
                allowChildren
                  ? {
                      slug: top.slug,
                      body: top.body,
                      children: top.children.map((c) => ({ ...c, children: [] })),
                    }
                  : { slug: top.slug, body: top.body },
              );
            },
          });
        },
        requestEntries: (item, apply) =>
          setProposal({
            title: `Suggest entries for “${item.name}”`,
            applyLabel: 'Add entries',
            // Diff the whole item: the body loses the per-instance bullets (red)
            // and the named entries are added as children (green).
            original: serializeItems({ preamble: '', items: [item] }),
            generate: async () => {
              const { body, entries } = await proposeItemEntries(gameId, {
                projectName: projectName ?? null,
                sectionTitle: sectionLabel,
                groupName: null,
                itemName: item.name,
                slug: item.slug,
                body: item.body,
              });
              const proposedItem: DesignItem = {
                ...item,
                body,
                children: [...item.children, ...entries],
              };
              return serializeItems({ preamble: '', items: [proposedItem] });
            },
            apply: (text) => {
              const top = parseItems(text).items[0];
              if (!top) return;
              apply({
                body: top.body,
                children: top.children.map((c) => ({ ...c, children: [] })),
              });
            },
          }),
      }
    : undefined;

  return (
    <div className="min-h-0 flex-1 overflow-y-auto p-4" data-testid="design-section-items">
      {/* Optional section intro (preamble prose under the H1 title). */}
      {pre.intro || introOpen ? (
        <div className="mb-4 flex h-40 flex-col overflow-hidden rounded border border-crucible-border bg-crucible-bg">
          <SectionWysiwyg
            value={pre.intro}
            onChange={(intro) => commit({ ...section, preamble: joinPreamble({ ...pre, intro }) })}
          />
        </div>
      ) : (
        <button
          type="button"
          data-testid="design-items-add-intro"
          onClick={() => setIntroOpen(true)}
          className="mb-4 flex items-center gap-1 text-xs text-crucible-muted hover:text-crucible-fg"
        >
          <Plus size={12} /> Add intro text
        </button>
      )}

      {hostsGameLoop && !hasGameLoop && (
        <button
          type="button"
          data-testid="design-items-add-game-loop"
          onClick={addGameLoop}
          className="mb-3 flex w-full items-center gap-1.5 rounded-md border border-dashed border-crucible-accent/50 bg-crucible-accent/5 px-3 py-2 text-sm text-crucible-accent hover:bg-crucible-accent/10"
        >
          <Plus size={14} /> Add Game Loop — the core moment-to-moment loop this design hangs off
        </button>
      )}

      <div className="space-y-3">
        {section.items.map((item, i) => (
          <ItemCard
            key={item.id}
            item={item}
            level={2}
            groupName={null}
            expanded={expanded}
            toggleExpanded={toggleExpanded}
            detailsExpanded={detailsExpanded}
            toggleDetails={toggleDetails}
            changedIds={changedIds}
            ai={ai}
            testId={`design-item-${i}`}
            locked={hostsGameLoop && isGameLoopItem(item.name)}
            onChange={(next) => setItems(replaceAt(section.items, i, next))}
            onDelete={() => setItems(removeAt(section.items, i))}
            onMove={(delta) => setItems(move(section.items, i, delta))}
            onAddChild={() => addChildTo(i)}
          />
        ))}
      </div>

      {section.items.length === 0 && (
        <p className="mb-3 text-sm text-crucible-muted" data-testid="design-items-empty">
          No items yet. Add one to break this section into discrete, top-down pieces.
        </p>
      )}

      <div className="mt-4 flex items-center gap-2">
        <button
          type="button"
          data-testid="design-items-add-item"
          onClick={addItem}
          className="flex items-center gap-1 rounded border border-crucible-border px-2.5 py-1 text-xs text-crucible-fg hover:bg-crucible-bg-elevated"
        >
          <Plus size={13} /> Add item
        </button>
        <button
          type="button"
          data-testid="design-items-add-group"
          onClick={addGroup}
          className="flex items-center gap-1 rounded border border-crucible-border px-2.5 py-1 text-xs text-crucible-muted hover:bg-crucible-bg-elevated hover:text-crucible-fg"
        >
          <Plus size={13} /> Add group
        </button>
      </div>

      {proposal && (
        <ItemProposalDialog
          open
          title={proposal.title}
          applyLabel={proposal.applyLabel}
          instruction={proposal.instruction}
          original={proposal.original}
          generate={proposal.generate}
          onApply={proposal.apply}
          onOpenChange={(o) => {
            if (!o) setProposal(null);
          }}
        />
      )}
    </div>
  );
}
